import * as React from "react";

import { authApi } from "@/api/auth";
import { ApiError } from "@/api/client";
import { ErrorBoundary } from "@/components/error-boundary";
import { AppShell } from "@/components/layout/AppShell";
import { LoadingPage } from "@/components/ui/loading-page";
import { AdminPage } from "@/pages/admin/AdminPage";
import { AuditLogsPage } from "@/pages/admin/AuditLogsPage";
import { BackupRestorePage } from "@/pages/admin/BackupRestorePage";
import { CostDashboardPage } from "@/pages/admin/CostDashboardPage";
import { DashboardDetailPage } from "@/pages/admin/DashboardDetailPage";
import { DashboardsListPage } from "@/pages/admin/DashboardsListPage";
import { ExternalSystemsPage } from "@/pages/admin/ExternalSystemsPage";
import { AdminInsightsPage } from "@/pages/admin/AdminInsightsPage";
import { HealthMonitorPage } from "@/pages/admin/HealthMonitorPage";
import { LocalLLMPage } from "@/pages/admin/LocalLLMPage";
import { UsersPage } from "@/pages/admin/UsersPage";
import { InsightsPage } from "@/pages/me/InsightsPage";
import { OdooModulesPage } from "@/pages/admin/OdooModulesPage";
import { FormBuilder } from "@/pages/admin/FormBuilder";
import { FormsPage } from "@/pages/admin/FormsPage";
import { NotificationsPage } from "@/pages/admin/NotificationsPage";
import { TranslationsPage } from "@/pages/admin/TranslationsPage";
import { WorkflowDesigner } from "@/pages/admin/WorkflowDesigner";
import { WorkflowsPage } from "@/pages/admin/WorkflowsPage";
import { ChatPage } from "@/pages/ChatPage";
import { ForgotPasswordPage } from "@/pages/ForgotPasswordPage";
import { LoginPage } from "@/pages/LoginPage";
import { ResetPasswordPage } from "@/pages/ResetPasswordPage";
import { SetupAccountPage } from "@/pages/SetupAccountPage";
import { SettingsLayout } from "@/pages/settings/SettingsLayout";
import { useAuthStore } from "@/stores/auth";
import { useRouteStore } from "@/stores/route";
import { useUIStore } from "@/stores/ui";

/**
 * App entry — chuyển giữa Login và Chat dựa vào auth state.
 *
 * On mount: thử refresh token để tự đăng nhập lại nếu cookie còn hạn.
 */
export default function App(): JSX.Element {
  const theme = useUIStore((s) => s.theme);
  const { isAuthenticated, isLoading, setAuth, clearAuth } = useAuthStore();

  React.useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
  }, [theme]);

  React.useEffect(() => {
    void (async () => {
      try {
        const tokens = await authApi.refresh();
        useAuthStore.setState({ accessToken: tokens.access_token });
        const user = await authApi.me();
        setAuth(user, tokens.access_token);
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) {
          clearAuth();
        } else {
          clearAuth();
        }
      }
    })();
  }, [setAuth, clearAuth]);

  // Doc 50/54: public routes accessible without auth
  const hash = typeof window !== "undefined" ? window.location.hash : "";
  const isSetupAccountRoute = hash.startsWith("#/setup-account");
  const isForgotPasswordRoute = hash.startsWith("#/forgot-password");
  const isResetPasswordRoute = hash.startsWith("#/reset-password");
  const isPublicAuthRoute =
    isSetupAccountRoute || isForgotPasswordRoute || isResetPasswordRoute;

  if (isLoading && !isPublicAuthRoute) {
    return <LoadingPage label="Đang khởi tạo phiên..." />;
  }

  if (isPublicAuthRoute && !isAuthenticated) {
    if (isSetupAccountRoute) return <SetupAccountPage />;
    if (isForgotPasswordRoute) return <ForgotPasswordPage />;
    if (isResetPasswordRoute) return <ResetPasswordPage />;
  }

  if (!isAuthenticated) {
    return <LoginPage />;
  }

  return (
    <ErrorBoundary>
      <AppShell>
        <RoutedView />
      </AppShell>
    </ErrorBoundary>
  );
}

function RoutedView(): JSX.Element {
  const route = useRouteStore((s) => s.current);
  const param = useRouteStore((s) => s.param);
  if (route === "admin.forms.edit" && param) {
    return <FormBuilder code={param} />;
  }
  if (route === "admin.forms") {
    return <FormsPage />;
  }
  if (route === "admin.workflows.edit" && param) {
    return <WorkflowDesigner code={param} />;
  }
  if (route === "admin.workflows") {
    return <WorkflowsPage />;
  }
  if (route === "admin.notifications") {
    return <NotificationsPage />;
  }
  if (route === "admin.translations") {
    return <TranslationsPage />;
  }
  if (route === "admin.audit") {
    return <AuditLogsPage />;
  }
  if (route === "admin.odooModules") {
    return <OdooModulesPage />;
  }
  if (route === "admin.externalSystems") {
    return <ExternalSystemsPage />;
  }
  if (route === "admin.health") {
    return <HealthMonitorPage />;
  }
  if (route === "admin.cost") {
    return <CostDashboardPage />;
  }
  if (route === "admin.localLlm") {
    return <LocalLLMPage />;
  }
  if (route === "admin.insights") {
    return <AdminInsightsPage />;
  }
  if (route === "admin.users") {
    return <UsersPage />;
  }
  if (route === "me.insights") {
    return <InsightsPage />;
  }
  if (route === "admin.backup") {
    return <BackupRestorePage />;
  }
  if (route === "admin.dashboards") {
    return <DashboardsListPage />;
  }
  if (route === "admin.dashboards.detail" && param) {
    return <DashboardDetailPage code={param} />;
  }
  if (route === "admin") {
    return <AdminPage />;
  }
  if (route.startsWith("settings")) {
    return <SettingsLayout />;
  }
  return <ChatPage />;
}
